import * as React from 'react';
import { useId } from '../hooks/use-id';
import { useControlledState } from '../hooks/use-controlled-state';
import { composeEventHandlers } from '../utils/compose-event-handlers';
import { useTreeProps, useTreeTriggerProps, useTreeNodeProps } from './tree.types';

export function useTree(props: useTreeProps) {
    const { openNodes, selectedNode, onChange, defaultOpenNodes = [] } = props;
    const [indexes, setIndexes] = useControlledState(openNodes, defaultOpenNodes, onChange);
    const [selectedNodeId, setSelectedNodeId] = useControlledState(selectedNode, null, null);

    const titlePrefixId = useId();
    const nodePrefixId = useId();

    // If a selectedNode is passed initially, we need to make sure the tree is fully opened to that node
    React.useEffect(() => {
        if (selectedNode) {
            let newIndexes = [...indexes];
            const nodes = selectedNode.split('.');

            let currentNode: string = null;
            for (const node of nodes) {
                currentNode = currentNode ? `${currentNode}.${node}` : node;
                newIndexes = checkNodeId(currentNode)
                    ? newIndexes.filter((d) => d != currentNode)
                    : [...newIndexes, currentNode];
                setIndexes([...new Set(newIndexes as Array<string>)], currentNode);
            }
        }
    }, []);

    const checkNodeId = React.useCallback(
        (index) => {
            return Array.isArray(indexes) ? indexes.includes(index) : indexes === index;
        },
        [indexes],
    );

    const checkSelectedIndex = React.useCallback(
        (index) => {
            return Array.isArray(selectedNodeId) ? selectedNodeId.includes(index) : selectedNodeId === index;
        },
        [selectedNodeId],
    );

    const getTriggerProps = React.useCallback(
        ({ nodeId, disabled, onClick, ...passThroughProps }: useTreeTriggerProps) => {
            const onInternalOnClick = () => {
                let newIndexes = [...indexes];

                newIndexes = checkNodeId(nodeId) ? newIndexes.filter((d) => d != nodeId) : [...newIndexes, nodeId];
                setIndexes([...new Set(newIndexes as Array<string>)], nodeId);
                setSelectedNodeId(nodeId);
            };

            return {
                disabled,
                id: `${titlePrefixId}_trigger_${nodeId}`,
                onClick: composeEventHandlers(onClick, onInternalOnClick),
                'aria-expanded': checkNodeId(nodeId),
                'aria-controls': `${nodePrefixId}_node_${nodeId}`,
                'aria-disabled': disabled,
                'data-testid': `tree-trigger-${nodeId}`,
                ...passThroughProps,
            };
        },
        [indexes, nodePrefixId, titlePrefixId, setIndexes, checkNodeId],
    );

    const getNodeProps = React.useCallback(
        ({ nodeId, ...passThroughProps }: useTreeNodeProps) => {
            return {
                role: 'region',
                id: `${nodePrefixId}_node_${nodeId}`,
                'aria-labelledby': `${titlePrefixId}_trigger_${nodeId}`,
                'data-testid': `tree-node-${nodeId}`,
                ...passThroughProps,
            };
        },
        [nodePrefixId, titlePrefixId],
    );

    return React.useMemo(
        () => ({
            openIndex: indexes,
            checkIsNodeVisible: checkNodeId,
            checkIsNodeSelected: checkSelectedIndex,
            onChange,
            getTriggerProps,
            getNodeProps,
        }),
        [indexes, checkNodeId, onChange, getTriggerProps, getNodeProps],
    );
}
